import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
// Removed API_ENDPOINTS import as it wasn't used directly in the component code provided
// import API_ENDPOINTS from '../ApiEndPoints';
import './DataTableStyles.css';

function DataTable(props) {
  const {
    apiEndpoint,
    userApiEndpoint,
    fields = [],
    actionButtons = [],
    userIdFields = [],
    enableUserFilter = false,
    defaultItemsPerPage = 10,
    defaultSortField = 'title',
    defaultSortOrder = 'asc',
    defaultUserId = '',
    title = "Data Table",
    itemsPerPageOptions = [10, 25, 50, 100],
    sortableFields = [],
    userFilterLabel = "Filter by user:",
    defaultMaxLength, // Added defaultMaxLength prop
    actionButtonsConfig, // Added actionButtonsConfig prop
  } = props;

  // --- State using useState ---
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true); // Start loading initially
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [users, setUsers] = useState({}); // For user ID lookup
  const [userOptions, setUserOptions] = useState([]); // For filter dropdown
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [selectedUserId, setSelectedUserId] = useState(defaultUserId);

  // Ref for potential useLayoutEffect measurements
  const tableContainerRef = useRef(null);

  // --- Data Fetching Logic using useCallback and useEffect ---

  // Memoize fetchItems to avoid re-creating it on every render
  const fetchItems = useCallback((page = 1, isRefreshing = false) => {
    // Check if apiEndpoint is properly set
    if (!apiEndpoint) {
      console.error("Error: apiEndpoint prop is not provided to DataTable component");
      setError("API endpoint not specified");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    console.log(`DataTable fetching items. Page: ${page}, Refreshing: ${isRefreshing}, Endpoint: ${apiEndpoint}`);

    // Use refreshing state for refresh operations, loading state for initial/major changes
    if (isRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true); // Set loading for non-refresh fetches (initial, filter/sort change)
    }
    setError(null); // Clear previous errors
    // setItems([]); // Optionally clear items immediately for a cleaner loading state

    const params = new URLSearchParams();
    params.append('page', page);
    params.append('per_page', itemsPerPage);
    if (searchTerm) params.append('search', searchTerm);
    if (sortField) params.append('sort', sortField);
    if (sortOrder) params.append('order', sortOrder);
    if (selectedUserId) params.append('owner_id', selectedUserId);

    const endpoint = `${apiEndpoint}?${params.toString()}`;
    console.log(`DataTable fetching data from: ${endpoint}`);

    fetch(endpoint)
      .then((response) => {
        console.log(`API response status: ${response.status}`);
        if (!response.ok) {
          // Try to get error message from response body
          return response.text().then(text => {
             throw new Error(`API responded with status ${response.status}. Body: ${text}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log("API data received:", data);
        if (!data || !data.data || !data.pagination) {
          console.error("API response does not have the expected structure:", data);
          throw new Error("Invalid API response format");
        }

        setItems(data.data);
        setCurrentPage(data.pagination.current_page);
        setTotalPages(data.pagination.total_pages);
        setHasNextPage(data.pagination.current_page < data.pagination.total_pages);
        setHasPreviousPage(data.pagination.current_page > 1);

        // Fetch user details if needed (can be optimized)
        if (userIdFields && userIdFields.length > 0 && userApiEndpoint) {
             // Always fetch user details when we get new data that might contain new user IDs
            fetchUserDetails();
        }
      })
      .catch((error) => {
        console.error("Error fetching list data:", error);
        setError(`Data fetch error: ${error.message}`);
      })
      .finally(() => {
         // Ensure loading states are turned off regardless of success/failure
         setLoading(false);
         setRefreshing(false);
      });
  // Dependencies for useCallback: Removed 'users' as it's not directly used in fetchItems
  }, [apiEndpoint, itemsPerPage, searchTerm, sortField, sortOrder, selectedUserId, userIdFields, userApiEndpoint]); // Removed users from dependency


  // --- Fetch User Details (for display name lookup) ---
  const fetchUserDetails = useCallback(() => {
    if (!userApiEndpoint) return;
    console.log("Fetching user details from:", userApiEndpoint);
    // Assuming endpoint returns ALL users - might need pagination if large
    fetch(`${userApiEndpoint}`) // Adjust if your user endpoint needs parameters
      .then(response => response.json())
      .then(data => {
        // Assuming data structure like { data: { data: [{ id: 1, name: 'John' }, ...] } }
        if (data?.data?.data && Array.isArray(data.data.data)) {
            const usersMap = data.data.data.reduce((acc, user) => {
                acc[user.id] = user;
                return acc;
            }, {});
            console.log("User details fetched and mapped:", usersMap);
            setUsers(usersMap);
        } else {
            console.warn("User details response structure not as expected:", data);
        }
      })
      .catch(error => {
        console.error("Error fetching user details:", error);
        // Optional: setError(`Failed to fetch user details: ${error.message}`);
      });
  }, [userApiEndpoint]);

  // --- Fetch User Options (for filter dropdown) ---
   const fetchUserOptions = useCallback(() => {
    if (!enableUserFilter || !userApiEndpoint) return;
    console.log("Fetching user options from:", userApiEndpoint);
     // Assuming endpoint returns ALL users - might need pagination if large
    fetch(`${userApiEndpoint}`) // Adjust if your user endpoint needs parameters
      .then(response => response.json())
      .then(data => {
         // Assuming data structure like { data: { data: [{ id: 1, name: 'John' }, ...] } }
        if (data?.data?.data && Array.isArray(data.data.data)) {
          console.log("User options fetched:", data.data.data);
          setUserOptions(data.data.data);
        } else {
            console.warn("User options response structure not as expected:", data);
        }
      })
      .catch(error => {
        console.error("Error fetching user options:", error);
        // Optional: setError(`Failed to fetch user options: ${error.message}`);
      });
  }, [enableUserFilter, userApiEndpoint]);


  // --- useEffect Hooks for Side Effects ---

  // Initial data fetch and fetching user options on mount
  useEffect(() => {
    console.log("DataTable initial mount effect running.");
    fetchItems(1); // Fetch page 1 on initial mount
    if (enableUserFilter) {
        fetchUserOptions();
    }
    // No cleanup needed here unless fetch needs cancellation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems, fetchUserOptions, enableUserFilter]); // fetchItems/fetchUserOptions are stable due to useCallback


   // Effect to re-fetch data when relevant state changes (pagination, sort, filter, search, itemsPerPage)
   // We separate this from the initial mount effect to avoid fetching twice initially.
   // The fetchItems function itself handles the logic based on current state.
   // Note: The actual triggering is handled by the state setters calling fetchItems,
   // but this effect structure is common for reacting to prop changes like apiEndpoint.
  useEffect(() => {
      console.log("DataTable dependency change effect running (apiEndpoint).");
      // If the apiEndpoint prop changes, fetch from the new endpoint, resetting to page 1
      fetchItems(1);
      // Also refetch user options if filter enabled and endpoint changes
      if (enableUserFilter) {
          fetchUserOptions();
      }
  }, [apiEndpoint, enableUserFilter, fetchItems, fetchUserOptions]); // Re-run if these props/callbacks change


  // --- useLayoutEffect Example ---
  // This runs *synchronously* after DOM updates but *before* paint.
  // Use cases: DOM measurements, applying styles that *must* be set before paint to avoid flicker.
  // For typical data loading flicker, this often isn't the main solution.
  useLayoutEffect(() => {
    if (loading || refreshing) {
      // Potential use: Measure something or apply a class synchronously
      // console.log('useLayoutEffect: Component is loading or refreshing. Applying styles PRE-PAINT.');
      // Example: Maybe add a class to the body or a container to prevent scrolling during load?
      // document.body.style.overflow = 'hidden';
      if(tableContainerRef.current) {
        // Example: Force a specific min-height *before* paint to reduce layout shift
        // This is often better handled with CSS, but demonstrates the timing.
        // tableContainerRef.current.style.minHeight = '300px'; // Adjust value as needed
      }
    } else {
      // Cleanup the effect when loading/refreshing is done
      // console.log('useLayoutEffect: Component finished loading/refreshing. Cleaning up PRE-PAINT.');
      // document.body.style.overflow = ''; // Restore scrolling
       if(tableContainerRef.current) {
         // tableContainerRef.current.style.minHeight = ''; // Remove the forced height
      }
    }

    // Cleanup function for when the component unmounts or dependencies change
    return () => {
       // console.log('useLayoutEffect: Cleanup.');
      // document.body.style.overflow = ''; // Ensure cleanup on unmount too
       if(tableContainerRef.current) {
         // tableContainerRef.current.style.minHeight = '';
      }
    };
  }, [loading, refreshing]); // Run this layout effect whenever loading or refreshing state changes


  // --- Event Handlers (using useCallback for performance if passed down) ---

  const refreshData = useCallback(() => {
    setSelectedItems([]); // Clear selections
    fetchItems(1, true); // Fetch page 1, mark as refreshing
  }, [fetchItems]); // Depends on the stable fetchItems

  const loadNextPage = useCallback(() => {
    if (hasNextPage) {
      setSelectedItems([]);
      fetchItems(currentPage + 1, true); // Use refreshing = true for smoother transition
    }
  }, [hasNextPage, currentPage, fetchItems]);

  const loadPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setSelectedItems([]);
      fetchItems(currentPage - 1, true); // Use refreshing = true for smoother transition
    }
  }, [hasPreviousPage, currentPage, fetchItems]);

  const handleItemsPerPageChange = useCallback((e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setSelectedItems([]);
    // Fetch items will be triggered by the useEffect watching itemsPerPage state change if structured that way,
    // OR call fetchItems directly if you prefer explicit calls on handlers. Let's call explicitly here for clarity.
    fetchItems(1, true); // Reset to page 1 with new count
  }, [fetchItems]); // Depends on fetchItems

  const toggleItemSelection = useCallback((itemId) => {
    setSelectedItems(prevSelectedItems => {
      const newSelectedItems = [...prevSelectedItems];
      const index = newSelectedItems.indexOf(itemId);
      if (index === -1) {
        newSelectedItems.push(itemId);
      } else {
        newSelectedItems.splice(index, 1);
      }
      return newSelectedItems;
    });
  }, []); // No dependencies needed if only using the setter's functional update

  const toggleSelectAll = useCallback(() => {
      if (items.length === 0) return; // Do nothing if no items
      if (selectedItems.length === items.length) {
          setSelectedItems([]); // Deselect all
      } else {
          setSelectedItems(items.map(item => item.id)); // Select all current items
      }
  }, [items, selectedItems.length]); // Depends on items and selectedItems


  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to page 1 for new search
    setSelectedItems([]);
    fetchItems(1, true);
  }, [fetchItems]);

  const handleSortFieldChange = useCallback((e) => {
    setSortField(e.target.value);
    setCurrentPage(1);
    setSelectedItems([]);
     fetchItems(1, true); // Fetch based on new sort field
  }, [fetchItems]);

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'));
    setCurrentPage(1);
    setSelectedItems([]);
     fetchItems(1, true); // Fetch based on new sort order
  }, [fetchItems]);

  const handleHeaderClick = useCallback((fieldKey) => {
     let newSortField = fieldKey;
     let newSortOrder = 'asc';

    setSortField(prevSortField => {
        if (prevSortField === fieldKey) {
            // If clicking the same column, toggle sort order
            setSortOrder(prevOrder => {
                newSortOrder = prevOrder === 'asc' ? 'desc' : 'asc';
                return newSortOrder;
            });
        } else {
             // If clicking a different column, sort by that column ascending
            setSortOrder('asc'); // newSortOrder is already 'asc'
        }
        return newSortField; // Set the new sort field
    });

    setCurrentPage(1);
    setSelectedItems([]);
    // Fetch items *after* state updates (fetchItems reads the latest state)
    // Note: Because setState is async, calling fetchItems immediately might not have the *very latest* state.
    // It's often better to rely on useEffect to trigger the fetch when sortField/sortOrder change.
    // However, explicit call here ensures it happens on click.
    // We might need a small delay or rely on the useEffect that watches sortField/sortOrder.
    // Let's keep the explicit call for now, assuming fetchItems grabs the latest state correctly.
    fetchItems(1, true);

  }, [fetchItems]); // Depends on fetchItems

  const handleUserFilterChange = useCallback((e) => {
    setSelectedUserId(e.target.value);
    setCurrentPage(1);
    setSelectedItems([]);
    fetchItems(1, true);
  }, [fetchItems]);


  // --- Helper Functions ---

  // No need for useCallback unless these funcs are very complex or passed as props
  const formatTruncatedValue = (value, field) => {
    const maxLength = field.maxLength || defaultMaxLength || 100;

    if (typeof value !== 'string' || value.length <= maxLength) {
      return value;
    }

    const truncatedValue = value.substring(0, maxLength) + '...';
    return (
      <span
        title={value}
        className="reactUiDataTable-truncatedText"
        data-full-text={value}
      >
        {truncatedValue}
      </span>
    );
  };

  const getDisplayValue = useCallback((item, field) => {
    // For user ID fields - use the users map fetched earlier
    if (userIdFields && userIdFields.includes(field.key)) {
      const userId = item[field.key];
      // Use the users state which is a map { userId: {id, name,...} }
      return users[userId]?.name || `User ${userId}` || ''; // Handle missing user
    }

    const value = item[field.key];

    if (value === null || value === undefined) return '';

    const isDateField = field.type === 'date' ||
                        field.key.toLowerCase().includes('date') ||
                        field.key === 'created_at' ||
                        field.key === 'updated_at';

    if (isDateField && value) {
      try {
        const date = new Date(value);
        if (!isNaN(date)) return date.toISOString().split('T')[0]; // Format yyyy-mm-dd
      } catch (e) { console.error(`Error formatting date for field ${field.key}:`, e); }
    }

    if (typeof value === 'string') {
      return formatTruncatedValue(value, field); // Use truncation helper
    }

    return value; // Return numbers, booleans, etc., as is
  }, [users, userIdFields, defaultMaxLength]); // Dependency: users map, userIdFields prop


  // --- Render Logic ---

  // Display error message if there is one
  if (error) {
    return (
      <div className="reactUiDataTable">
        <h1 className="reactUiDataTable-title">{title}</h1>
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button
            onClick={refreshData} // Use the memoized handler
            className="ml-2 btn btn-sm btn-danger"
          >
            Retry
          </button>
        </div>
        <div className="mt-3 card">
            <div className="card-header">Debug Information</div>
            <div className="card-body">
              <p><strong>API Endpoint:</strong> {apiEndpoint || 'Not Provided'}</p>
              <p><strong>Component Props:</strong></p>
              <pre>{JSON.stringify(props, null, 2)}</pre>
              <p><strong>Current State Snapshot:</strong></p>
              <pre>{JSON.stringify({ currentPage, itemsPerPage, searchTerm, sortField, sortOrder, selectedUserId, loading, refreshing, totalPages }, null, 2)}</pre>
            </div>
          </div>
      </div>
    );
  }

  // Determine effective sortable fields
  const currentSortableFields = sortableFields.length > 0 ? sortableFields : fields;


  return (
    <div className="reactUiDataTable">
        <div className="reactUiDataTable-header">
          <h2 className="reactUiDataTable-title">{title}</h2>
          <div className="reactUiDataTable-controls">
            {/* Search Form */}
            <form onSubmit={handleSearchSubmit} className="reactUiDataTable-searchForm">
              <input
                type="text"
                className="reactUiDataTable-searchInput"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}

              />
              <button type="submit" className="reactUiDataTable-searchButton" disabled={loading || refreshing}>
                Search
              </button>
            </form>

             {/* User Filter */}
            {enableUserFilter && (
              <div className="reactUiDataTable-userFilter">
                <label htmlFor="userFilterSelect">{userFilterLabel}</label>
                <select
                  id="userFilterSelect"
                  className="reactUiDataTable-select"
                  value={selectedUserId}
                  onChange={handleUserFilterChange}
                   disabled={loading || refreshing} // Disable during load/refresh
                >
                  <option value="">All Users</option>
                  {Array.isArray(userOptions) && userOptions.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || `User ${user.id}`} {/* Fallback name */}
                    </option>
                  ))}
                </select>
              </div>
            )}

             {/* Sort Controls */}
            <div className="reactUiDataTable-sortControls">
              <label htmlFor="sortFieldSelect">Sort by:</label>
              <select
                id="sortFieldSelect"
                className="reactUiDataTable-select"
                value={sortField}
                onChange={handleSortFieldChange}
                 disabled={loading || refreshing} // Disable during load/refresh
              >
                {currentSortableFields.map(field => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>

              <button
                className="reactUiDataTable-sortOrderButton"
                onClick={toggleSortOrder}
                title={`Current: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}. Click to switch.`}
                 disabled={loading || refreshing} // Disable during load/refresh
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>

            {/* Items Per Page */}
            <div className="reactUiDataTable-itemsPerPage">
              <label htmlFor="itemsPerPageSelect">Items per page:</label>
              <select
                id="itemsPerPageSelect"
                className="reactUiDataTable-select"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                 disabled={loading || refreshing} // Disable during load/refresh
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

             {/* Refresh Button */}
            <button
              className="reactUiDataTable-refreshButton"
              onClick={refreshData}
               disabled={loading || refreshing} // Disable if already loading/refreshing
            >
              {refreshing ? 'Refreshing...' : (loading ? 'Loading...' : 'Refresh')}
            </button>
          </div>
        </div>

      {/* --- Loading / Refreshing Indicator --- */}
      {/* Show only one indicator, prioritize 'refreshing' if both might be true */}
      {refreshing && (
          <div className="reactUiDataTable-loadingIndicator">Refreshing data...</div>
      )}
      {!refreshing && loading && (
           <div className="reactUiDataTable-loadingIndicator">Loading data, please wait...</div>
      )}

      {/* --- Table Content (only render when not initial loading) --- */}
      {/* We render the structure even during refresh to avoid layout jumps */}
      {(!loading || refreshing) && (
         <div ref={tableContainerRef} className={`reactUiDataTable-tableContainer ${refreshing ? 'reactUiDataTable-tableContainer--refreshing' : ''}`}>
          <table className="reactUiDataTable-table">
            <thead className="reactUiDataTable-thead">
              <tr className="reactUiDataTable-headerRow">
                <th className="reactUiDataTable-th reactUiDataTable-checkboxColumn">
                  <input
                    type="checkbox"
                    className="reactUiDataTable-checkbox"
                    checked={items.length > 0 && selectedItems.length === items.length}
                    onChange={toggleSelectAll}
                    disabled={items.length === 0 || loading || refreshing} // Disable if no items or loading
                  />
                </th>
                 {fields.map((field) => {
                    // Check against the effective sortable fields list
                    const isSortable = currentSortableFields.some(sf => sf.key === field.key);
                    const isCurrentSortField = sortField === field.key;
                    const headerClasses = [
                        'reactUiDataTable-th',
                        isSortable ? 'reactUiDataTable-sortable' : '',
                        isCurrentSortField ? 'reactUiDataTable-sorted' : ''
                    ].filter(Boolean).join(' '); // Filter out empty strings

                    return (
                        <th
                        key={field.key}
                        className={headerClasses}
                        onClick={isSortable && !loading && !refreshing ? () => handleHeaderClick(field.key) : undefined} // Prevent click during load
                        title={isSortable ? `Sort by ${field.label}` : field.label}
                        aria-sort={isCurrentSortField ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'} // Accessibility
                        >
                        <div className="reactUiDataTable-headerContent">
                            <span>{field.label}</span>
                             {isSortable && (
                              <span className="reactUiDataTable-sortIndicator" aria-hidden="true">
                                {/* Display indicator based on sort state */}
                                {isCurrentSortField ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ' ⇅'}
                              </span>
                            )}
                        </div>
                        </th>
                    );
                })}
                {actionButtons.length > 0 && (
                  <th
                    className="reactUiDataTable-th reactUiDataTable-actionsColumn"
                    style={{
                      width: 'auto',
                      minWidth: actionButtons.length * 40 + 'px', // Adjust multiplier as needed
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="reactUiDataTable-tbody">
              {/* Handle case where fetch finished but returned no items */}
              {!loading && !refreshing && items.length === 0 ? (
                  <tr className="reactUiDataTable-noDataRow">
                    <td
                      colSpan={fields.length + 1 + (actionButtons.length > 0 ? 1 : 0)} // Checkbox + Fields + Actions
                      className="reactUiDataTable-td reactUiDataTable-noData"
                    >
                      No data available matching your criteria.
                    </td>
                  </tr>
                ) : (
                  // Render table rows
                   items.map((item, rowIndex) => (
                      <tr
                        key={item.id || rowIndex} // Fallback key if id is missing
                        className={`reactUiDataTable-tr ${rowIndex % 2 === 1 ? 'reactUiDataTable-evenRow' : 'reactUiDataTable-oddRow'} ${refreshing ? 'reactUiDataTable-tr--refreshing' : ''}`}
                      >
                        <td className="reactUiDataTable-td reactUiDataTable-checkboxCell">
                          <input
                            type="checkbox"
                            className="reactUiDataTable-checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                            disabled={loading || refreshing} // Disable during load
                          />
                        </td>
                        {fields.map((field) => (
                          <td
                            key={`${item.id}-${field.key}`}
                            className="reactUiDataTable-td"
                          >
                             {/* Use the memoized display value getter */}
                             {getDisplayValue(item, field)}
                          </td>
                        ))}
                        {actionButtons.length > 0 && (
                          <td className="reactUiDataTable-td reactUiDataTable-actionButtons">
                            <div className={actionButtonsConfig?.containerClass || 'reactUiDataTable-actionButtonsContainer'}>
                              {actionButtons.map((action, index) => (
                                <button
                                  key={index}
                                  onClick={() => action.onClick(item)}
                                  className={action.className || 'reactUiDataTable-actionButton'} // Provide default class
                                  title={action.title || action.label} // Use label as fallback title
                                   disabled={loading || refreshing || action.disabled} // Disable during load or if action itself is disabled
                                >
                                  {action.label}
                                </button>
                              ))}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                )}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Pagination (only show if not initial loading and more than 1 page) --- */}
       {!loading && totalPages > 0 && (
           <div className="reactUiDataTable-pagination">
              <div className="reactUiDataTable-pageInfo">
                <span className="reactUiDataTable-pageIndicator">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              <div className="reactUiDataTable-pageActions">
                <button
                  className="reactUiDataTable-paginationButton"
                  onClick={loadPreviousPage}
                   disabled={!hasPreviousPage || loading || refreshing} // Disable if no prev page or loading
                >
                  Previous
                </button>

                <button
                  className="reactUiDataTable-paginationButton"
                  onClick={loadNextPage}
                   disabled={!hasNextPage || loading || refreshing} // Disable if no next page or loading
                >
                  Next
                </button>
              </div>
            </div>
       )}

      {/* --- Selection Info (only show if not initial loading) --- */}
      {!loading && selectedItems.length > 0 && (
          <div className="reactUiDataTable-selectionInfo">
              <p>{selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected</p>
              {/* Add bulk actions here if needed */}
          </div>
       )}

    </div>
  );
}

export default DataTable;
